// eslint-disable-next-line import/no-deprecated
import {guidToInt} from '@wix/wixstores-client-storefront-sdk';
import {LineItemModel} from './LineItem.model';
import {AppliedCouponModel} from './AppliedCoupon.model';
import {ContactInfoModel} from './ContactInfo.model';
import {PriceModel} from './Price.model';
import {CartDiscount, Coupon, DiscountRule, LineItem, MultiCurrencyPrice} from '@wix/auto_sdk_ecom_current-cart';
import {IOptionsSelectionsValue, IRenderingConfig, ModelContext} from '../../types/app.types';
import {getUniques} from '../utils/getUniques';
import {SPECS} from '../specs';
import {CartWithPrivateFields} from '../../../__tests__/mocks/builders/sdk/cart.builder';

type CartDiscountWithPrivateFields = CartDiscount & {discountRule?: DiscountRule; lineItemIds: string[]};
export type LineItemWithPrivateFields = LineItem & {
  lineItemPrice: MultiCurrencyPrice;
};
type CouponWithPrivateFields = Coupon & {amount: MultiCurrencyPrice};

export class CartModel {
  public id: string;
  public lineItems: LineItemModel[];
  public lineItemsTypes: string[];
  public subtotal: PriceModel;
  public buyerNote: string;
  public currencyCode: string;
  public convertedCurrencyCode: string;
  public paymentCurrencyCode: string;
  public checkoutId: string;
  public appliedCoupon?: AppliedCouponModel;
  public purchaseFlowId: string;
  public contactInfo: ContactInfoModel;
  public subtotalAfterDiscounts: PriceModel;
  public discount?: PriceModel;
  public managedByV2?: boolean;

  constructor(
    params: Partial<CartModel>,
    {
      isGiftCardMixedCartEnabled,
      supportPaymentCurrency,
    }: {isGiftCardMixedCartEnabled: boolean; supportPaymentCurrency: boolean}
  ) {
    this.id = params.id;
    this.lineItems = params.lineItems;
    this.lineItemsTypes = isGiftCardMixedCartEnabled ? getUniques(params.lineItems.map(({itemType}) => itemType)) : [];
    this.subtotal = params.subtotal;
    this.buyerNote = params.buyerNote;
    this.currencyCode = params.currencyCode;
    this.paymentCurrencyCode =
      supportPaymentCurrency && params.paymentCurrencyCode === params.convertedCurrencyCode
        ? params.convertedCurrencyCode
        : params.currencyCode;
    this.convertedCurrencyCode = params.convertedCurrencyCode;
    this.checkoutId = params.checkoutId;
    this.appliedCoupon = params.appliedCoupon;
    this.purchaseFlowId = params.purchaseFlowId;
    this.contactInfo = params.contactInfo;
    this.subtotalAfterDiscounts = params.subtotalAfterDiscounts;
    this.discount = params.discount;
    this.managedByV2 = params.managedByV2;
  }

  public static fromSDK(
    cart: CartWithPrivateFields,
    renderingConfigMap: Record<string, IRenderingConfig> | undefined,
    bookingsOptionsMap: Record<string, IOptionsSelectionsValue[]> | undefined,
    modelContext: ModelContext
  ): CartModel {
    const lineItemIdToRules = mapLineItemIdToRules(cart.appliedDiscounts as CartDiscountWithPrivateFields[]);
    const appliedCoupon = cart.appliedDiscounts?.find((discount) => discount?.coupon)
      ?.coupon as CouponWithPrivateFields;
    return new CartModel(
      {
        id: cart._id,
        lineItems: cart.lineItems.map((lineItem: LineItem) =>
          LineItemModel.fromSDK(
            lineItem as LineItemWithPrivateFields,
            lineItemIdToRules[lineItem._id],
            modelContext,
            // eslint-disable-next-line import/no-deprecated
            renderingConfigMap?.[guidToInt(lineItem._id)],
            // eslint-disable-next-line import/no-deprecated
            bookingsOptionsMap?.[guidToInt(lineItem._id)]
          )
        ),
        subtotal: PriceModel.fromSDK(cart.subtotal, modelContext),
        buyerNote: cart.buyerNote,
        currencyCode: cart.currency,
        convertedCurrencyCode: cart.conversionCurrency,
        paymentCurrencyCode: cart.paymentCurrency,
        checkoutId: cart.checkoutId,
        appliedCoupon: appliedCoupon ? AppliedCouponModel.fromSDK(appliedCoupon) : undefined,
        purchaseFlowId: cart.purchaseFlowId,
        contactInfo: cart.contactInfo ? ContactInfoModel.fromSDK(cart.contactInfo) : undefined,
        subtotalAfterDiscounts: PriceModel.fromSDK(cart.subtotalAfterDiscounts, modelContext),
        discount: PriceModel.fromSDK(cart.discount, modelContext),
        managedByV2: cart.managedByV2,
      },
      {
        isGiftCardMixedCartEnabled: modelContext.siteStore.experiments.enabled(SPECS.GiftCardAddToCartSettings),
        supportPaymentCurrency: modelContext.siteStore.experiments.enabled(SPECS.SupportPaymentCurrency),
      }
    );
  }
}

function mapLineItemIdToRules(appliedDiscounts: CartDiscountWithPrivateFields[]): Record<string, string[]> {
  if (!appliedDiscounts) {
    return {};
  }
  return appliedDiscounts.reduce((acc, discount) => {
    if (!discount.discountRule) {
      return acc;
    }
    discount.lineItemIds?.forEach((lineItemId) => {
      acc[lineItemId] = acc[lineItemId] ?? [];
      acc[lineItemId].push(discount.discountRule.name.translated);
    });
    return acc;
  }, {});
}
